import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Receipt, AlertTriangle, X, Clock, Tag, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import type { Subscription, PaymentRecord } from '../types';
import { billingApi } from '../services/billing';

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  grace_period: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const PAYMENT_STATUS_BADGE: Record<string, string> = {
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Billing() {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const queryClient = useQueryClient();

  const { data: subscriptionData, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: billingApi.getSubscription,
  });

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['payment-history'],
    queryFn: billingApi.getPaymentHistory,
  });

  const generatePaymentMutation = useMutation({
    mutationFn: billingApi.generatePayment,
    onSuccess: (data) => {
      billingApi.submitPayFastForm(data.payment_data, data.payment_url);
    },
    onError: () => {
      setIsRedirecting(false);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason?: string) => billingApi.cancelSubscription(reason),
    onSuccess: () => {
      setShowCancelModal(false);
      setCancelReason('');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  const subscription: Subscription | null = subscriptionData?.subscription ?? null;
  const cameraCount: number = subscriptionData?.camera_count ?? 0;
  const pricePerCameraExclVat: string = subscriptionData?.price_per_camera_excl_vat ?? '75.00';
  const normalPriceExclVat: string = subscriptionData?.normal_price_per_camera_excl_vat ?? '99.00';

  const handleSubscribe = () => {
    setIsRedirecting(true);
    generatePaymentMutation.mutate();
  };

  const handleCancelConfirm = () => {
    cancelMutation.mutate(cancelReason.trim() || undefined);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Trial Banner */}
      {subscription?.trial_end && (() => {
        const trialEnd = new Date(subscription.trial_end);
        const now = new Date();
        const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / 86400000);
        const isActive = daysLeft > 0;
        return isActive ? (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${
            daysLeft <= 7
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          }`}>
            <Clock className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              daysLeft <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
            }`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                daysLeft <= 7 ? 'text-amber-800 dark:text-amber-300' : 'text-blue-800 dark:text-blue-300'
              }`}>
                Free Trial &mdash; {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
              </p>
              <p className={`text-sm mt-1 ${
                daysLeft <= 7 ? 'text-amber-700 dark:text-amber-400' : 'text-blue-700 dark:text-blue-400'
              }`}>
                Your trial expires on {trialEnd.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}.
                {daysLeft <= 7 ? ' Subscribe now to avoid losing access.' : ' Subscribe anytime to continue using Insights.'}
              </p>
            </div>
            {daysLeft <= 7 && (
              <Button size="sm" onClick={handleSubscribe} isLoading={generatePaymentMutation.isPending}>
                Subscribe Now
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Trial Expired
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                Your free trial ended on {trialEnd.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}.
                Subscribe to continue using Insights.
              </p>
            </div>
            <Button size="sm" onClick={handleSubscribe} isLoading={generatePaymentMutation.isPending}>
              Subscribe
            </Button>
          </div>
        );
      })()}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>
        <p className="text-gray-500 dark:text-slate-400">
          Manage your subscription and view payment history
        </p>
      </div>

      {/* Subscription Status */}
      <Card>
        <div className="flex items-center gap-2 mb-6">
          <CreditCard className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Subscription Status
          </h2>
        </div>

        {subLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-1/3" />
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
          </div>
        ) : subscription ? (
          <div className="space-y-4">
            {/* Status badge */}
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  STATUS_BADGE[subscription.status] || STATUS_BADGE.expired
                }`}
              >
                {subscription.status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              {subscription.cancel_at_period_end && (
                <span className="text-sm text-yellow-600 dark:text-yellow-400">
                  Cancels at end of period
                </span>
              )}
            </div>

            {/* Price breakdown */}
            <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-lg space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <Tag className="w-3 h-3" />
                  Limited Time Offer
                </span>
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  <span className="line-through">R{normalPriceExclVat}</span> &rarr; R{pricePerCameraExclVat}/camera/month excl. VAT
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-slate-300">
                <span className="font-medium">{cameraCount} camera{cameraCount !== 1 ? 's' : ''}</span>
                {' '}&times; R{pricePerCameraExclVat} + 15% VAT ={' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  R{subscription.total_amount}/month incl. VAT
                </span>
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Period: </span>
                  <span className="text-gray-900 dark:text-white">
                    {formatDate(subscription.current_period_start)} &ndash;{' '}
                    {formatDate(subscription.current_period_end)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Next billing: </span>
                  <span className="text-gray-900 dark:text-white">
                    {formatDate(subscription.next_billing_date)}
                  </span>
                </div>
                {subscription.cancelled_at && (
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Access until: </span>
                    <span className="text-gray-900 dark:text-white">
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {subscription.status === 'cancelled' ? (
                <Button
                  onClick={handleSubscribe}
                  isLoading={generatePaymentMutation.isPending}
                >
                  Resubscribe
                </Button>
              ) : (
                subscription.status === 'active' && (
                  <Button
                    variant="danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel Subscription
                  </Button>
                )
              )}
            </div>

            {generatePaymentMutation.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to generate payment. Please try again.
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-8 space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full">
              <CreditCard className="w-8 h-8 text-gray-400 dark:text-slate-500" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                No active subscription
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                Subscribe to start using Insights for your cameras.
              </p>
            </div>

            {/* Pricing card */}
            <div className="inline-block text-left max-w-sm w-full mx-auto">
              <div className="relative p-5 border-2 border-emerald-500 dark:border-emerald-400 rounded-xl bg-white dark:bg-slate-800">
                <span className="absolute -top-3 left-4 inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-bold bg-emerald-500 text-white">
                  <Tag className="w-3 h-3" />
                  Limited Time Offer
                </span>
                <div className="mt-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">R75</span>
                    <span className="text-sm text-gray-500 dark:text-slate-400">/camera/month</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    excl. VAT (R86.25 incl. VAT)
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-400 dark:text-slate-500 line-through">R99/camera/month</span>
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Save 24%</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">
                    All prices exclusive of 15% VAT. Billed monthly per camera.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSubscribe}
              isLoading={generatePaymentMutation.isPending}
              size="lg"
            >
              Subscribe Now
            </Button>
            {generatePaymentMutation.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to generate payment. Please try again.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Payment History */}
      <Card padding="none">
        <div className="flex items-center gap-2 px-6 pt-6 pb-4">
          <Receipt className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Payment History</h2>
        </div>

        {paymentsLoading ? (
          <div className="px-6 pb-6 animate-pulse space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        ) : payments && payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-slate-700">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Date
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Status
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Cameras
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Billing Period
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Transaction ID
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                {payments.slice(0, 50).map((payment: PaymentRecord) => (
                  <tr
                    key={payment.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      R{payment.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          PAYMENT_STATUS_BADGE[payment.status] || PAYMENT_STATUS_BADGE.pending
                        }`}
                        title={
                          payment.status === 'failed' && payment.failure_reason
                            ? payment.failure_reason
                            : undefined
                        }
                      >
                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-600 dark:text-slate-300 whitespace-nowrap">
                      {payment.camera_count_at_payment ?? '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-slate-300 whitespace-nowrap">
                      {payment.billing_period_start && payment.billing_period_end
                        ? `${formatDate(payment.billing_period_start)} - ${formatDate(payment.billing_period_end)}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400 font-mono whitespace-nowrap">
                      {payment.payfast_transaction_id || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 pb-6 text-center py-8">
            <p className="text-sm text-gray-500 dark:text-slate-400">No payment records found.</p>
          </div>
        )}
      </Card>

      {/* PayFast Redirect Overlay */}
      {isRedirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto" />
            <div>
              <p className="text-lg font-semibold text-white">Redirecting to PayFast</p>
              <p className="text-sm text-white/70 mt-1">Please wait while we prepare your payment...</p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowCancelModal(false)}
          />
          <div className="relative w-full max-w-md mx-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Cancel Subscription
                </h3>
              </div>
              <button
                onClick={() => setShowCancelModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Your subscription will remain active until the end of the current billing period.
                  No refunds will be issued.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Reason for cancelling (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Tell us why you're leaving..."
                  rows={3}
                  className="block w-full rounded-lg border px-3 py-2 transition-colors text-gray-900 placeholder-gray-400 bg-white dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-gray-300 dark:border-slate-600"
                />
              </div>

              {cancelMutation.isError && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Failed to cancel subscription. Please try again.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-slate-700">
              <Button variant="outline" onClick={() => setShowCancelModal(false)}>
                Close
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelConfirm}
                isLoading={cancelMutation.isPending}
              >
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
